export const MANUAL_BRIDGE_SOURCE = 'Manual Entry';

export const AUTHORIZATION_REPLACED_MESSAGE =
  'This authorization was replaced. Nothing was saved. Refresh the chart and select the current authorization.';

export const STALE_AUTHORIZATION_MESSAGE =
  'This authorization changed. Nothing was saved. Refresh the chart and review it again.';

export const BRIDGE_ALREADY_REPLACED_MESSAGE =
  'This bridge is already replaced. Nothing was changed.';

export const BRIDGE_REPLACED_MESSAGE =
  'The bridge was replaced. Linked services now use the imported authorization, and its used count is the number you entered.';

export const USAGE_CORRECTED_MESSAGE =
  'The used count is now the number you entered.';

export const IMPORT_OVERWRITE_NOTE =
  'A later changed WellSky import can replace this used count.';

export type AuthKindRow = {
  id?: string;
  client_id?: string;
  source?: string | null;
  auth_type?: string | null;
  auth_number?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  raw_auth_status?: string | null;
  wellsky_auth_id?: string | null;
  focus_auth_id?: string | null;
  status?: string | null;
};

export type LinkedServiceRow = {
  id: string;
  dos?: string | null;
  service_code?: string | null;
  status?: string | null;
};

export type BalanceEvidence = {
  totalUnits: number;
  unitsUsed: number;
  availableUnits: number | null;
};

export function isReplacedAuthorization(auth: { raw_auth_status?: string | null } | null | undefined): boolean {
  return String(auth?.raw_auth_status || '').trim().toUpperCase() === 'REPLACED';
}

export function isOpenManualBridge(auth: AuthKindRow | null | undefined): boolean {
  return Boolean(auth) && auth?.source === MANUAL_BRIDGE_SOURCE && !isReplacedAuthorization(auth);
}

export function isImportedAuthorization(auth: AuthKindRow | null | undefined): boolean {
  if (!auth || isReplacedAuthorization(auth)) return false;
  if (auth.wellsky_auth_id || auth.focus_auth_id) return true;
  return String(auth.source || '').toLowerCase().includes('wellsky');
}

export function userMayManageBridge(user: { role?: string; activeRole?: string } | null | undefined): boolean {
  const role = String(user?.role || '').toUpperCase();
  const active = String(user?.activeRole || '').toUpperCase();
  return role === 'OWNER' || role === 'ADMIN' || active === 'OWNER' || active === 'ADMIN';
}

export function parseCurrentUsedCount(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 100000) return value;
  if (typeof value === 'string' && /^(0|[1-9]\d*)$/.test(value.trim())) {
    const parsed = Number(value.trim());
    if (parsed <= 100000) return parsed;
  }
  return null;
}

export function normalizeAvailableUnits(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function normalizeBalance(row: {
  total_units?: unknown;
  totalUnits?: unknown;
  units_used?: unknown;
  unitsUsed?: unknown;
  available_units?: unknown;
  availableUnits?: unknown;
}): BalanceEvidence {
  return {
    totalUnits: Number(row.total_units ?? row.totalUnits ?? 0),
    unitsUsed: Number(row.units_used ?? row.unitsUsed ?? 0),
    availableUnits: normalizeAvailableUnits(row.available_units !== undefined ? row.available_units : row.availableUnits)
  };
}

export function balancesMatch(left: BalanceEvidence, right: BalanceEvidence): boolean {
  return left.totalUnits === right.totalUnits
    && left.unitsUsed === right.unitsUsed
    && left.availableUnits === right.availableUnits;
}

export function sameServiceIds(left: string[], right: string[]): boolean {
  const a = [...left].map(String).sort();
  const b = [...right].map(String).sort();
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

export function requiredAuthTypeForService(serviceCode: string | null | undefined): 'SEA' | 'STA' | 'FEA' | 'FTA' | null {
  if (serviceCode === '92523') return 'SEA';
  if (serviceCode === '92610') return 'FEA';
  if (serviceCode === '92507') return 'STA';
  if (serviceCode === '92526') return 'FTA';
  return null;
}

function dateOnly(value: string | null | undefined): string {
  return String(value || '').slice(0, 10);
}

export function bridgeCompatibilityProblems(
  bridge: AuthKindRow,
  replacement: AuthKindRow,
  services: LinkedServiceRow[]
): string[] {
  const problems: string[] = [];
  const bridgeType = String(bridge.auth_type || '').toUpperCase();
  const replacementType = String(replacement.auth_type || '').toUpperCase();
  if (!replacementType || replacementType === 'UNMAPPED') {
    problems.push('The imported authorization has no mapped service type.');
  } else if (bridgeType !== replacementType) {
    problems.push(`The bridge is ${bridgeType || 'unmapped'} and the imported authorization is ${replacementType}.`);
  }
  const start = dateOnly(replacement.start_date);
  const end = dateOnly(replacement.end_date);
  for (const service of services) {
    const required = requiredAuthTypeForService(service.service_code);
    if (required && replacementType && replacementType !== required) {
      problems.push(`Service ${service.service_code || service.id} requires a ${required} authorization. The imported authorization is ${replacementType}.`);
    }
    const dos = dateOnly(service.dos);
    if (!start || !end || !dos || dos < start || dos > end) {
      problems.push(`Service on ${dos || 'an unknown date'} (${service.service_code || service.id}) is outside the imported authorization dates (${start || 'missing'} to ${end || 'missing'}).`);
    }
  }
  return problems;
}

export function displayAvailableUnits(totalUnits: number, unitsUsed: number, availableUnits: number | null | undefined): number {
  if (availableUnits === null || availableUnits === undefined) return totalUnits - unitsUsed;
  return availableUnits;
}
